import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { BarChart3, Eye, Globe, TrendingUp, Loader2, Calendar, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface Statistics {
  totalViews: number;
  totalTranslations: number;
  qrScans: number;
  uniqueVisitors: number;
  avgViewsPerDay: number;
  viewsByLanguage: Array<{ language: string; count: number }>;
  translationsByLanguage: Array<{ language: string; count: number }>;
  viewsByDate: Array<{ date: string; count: number }>;
  qrScansByDate: Array<{ date: string; count: number }>;
  downloadsByDate: Array<{ date: string; count: number }>;
  peakHours: Array<{ hour: number; count: number }>;
  popularItems?: Array<{ name: string; views: number }>;
}

interface StatisticsDashboardProps {
  menuId?: string | number;
}

const StatisticsDashboard: React.FC<StatisticsDashboardProps> = ({ menuId }) => {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  useEffect(() => {
    if (menuId) {
      fetchStatistics();
    }
  }, [menuId, timeRange]);

  const fetchStatistics = async () => {
    if (!menuId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/menu/${menuId}/statistics`, {
        params: { timeRange },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error: any) {
      console.error('Failed to fetch statistics:', error);
      // Use mock data if API fails
      setStatistics({
        totalViews: 1250,
        totalTranslations: 342,
        qrScans: 89,
        uniqueVisitors: 756,
        avgViewsPerDay: 41.7,
        viewsByLanguage: [
          { language: 'it', count: 450 },
          { language: 'en', count: 380 },
          { language: 'fr', count: 220 },
          { language: 'es', count: 200 }
        ],
        translationsByLanguage: [
          { language: 'en', count: 120 },
          { language: 'fr', count: 85 },
          { language: 'es', count: 72 },
          { language: 'de', count: 65 }
        ],
        viewsByDate: [
          { date: '2024-01-01', count: 45 },
          { date: '2024-01-02', count: 52 },
          { date: '2024-01-03', count: 38 },
          { date: '2024-01-04', count: 61 },
          { date: '2024-01-05', count: 48 },
          { date: '2024-01-06', count: 55 },
          { date: '2024-01-07', count: 42 }
        ],
        qrScansByDate: [
          { date: '2024-01-01', count: 12 },
          { date: '2024-01-02', count: 8 },
          { date: '2024-01-03', count: 15 },
          { date: '2024-01-04', count: 10 },
          { date: '2024-01-05', count: 7 },
          { date: '2024-01-06', count: 11 },
          { date: '2024-01-07', count: 9 }
        ],
        downloadsByDate: [
          { date: '2024-01-01', count: 5 },
          { date: '2024-01-02', count: 3 },
          { date: '2024-01-03', count: 8 },
          { date: '2024-01-04', count: 6 },
          { date: '2024-01-05', count: 2 },
          { date: '2024-01-06', count: 4 },
          { date: '2024-01-07', count: 7 }
        ],
        peakHours: [
          { hour: 12, count: 89 },
          { hour: 13, count: 102 },
          { hour: 19, count: 76 },
          { hour: 20, count: 82 },
          { hour: 18, count: 68 }
        ],
        popularItems: [
          { name: 'Margherita Pizza', views: 234 },
          { name: 'Carbonara', views: 189 },
          { name: 'Tiramisu', views: 156 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const languageNames: Record<string, string> = {
    it: t('statistics.languages.it'),
    en: t('statistics.languages.en'),
    fr: t('statistics.languages.fr'),
    es: t('statistics.languages.es'),
    de: t('statistics.languages.de'),
    pt: t('statistics.languages.pt'),
    ru: t('statistics.languages.ru'),
    zh: t('statistics.languages.zh'),
    ar: t('statistics.languages.ar'),
    ja: t('statistics.languages.ja')
  };

  const viewsChartData = {
    labels: statistics?.viewsByDate?.map(d => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })) || [],
    datasets: [
      {
        label: t('statistics.charts.views_over_time'),
        data: statistics?.viewsByDate?.map(d => d.count) || [],
        backgroundColor: 'rgba(10, 12, 11, 0.1)',
        borderColor: '#0A0C0B',
        borderWidth: 2,
        tension: 0.4
      }
    ]
  };

  const languageViewsData = {
    labels: statistics?.viewsByLanguage?.map(v => languageNames[v.language] || v.language) || [],
    datasets: [
      {
        label: t('statistics.charts.views_by_language'),
        data: statistics?.viewsByLanguage?.map(v => v.count) || [],
        backgroundColor: [
          '#0A0C0B',
          '#7BD5B5',
          '#5a9e8a',
          '#687d76',
          '#3B82F6',
          '#10B981'
        ]
      }
    ]
  };

  const translationsData = {
    labels: statistics?.translationsByLanguage?.map(t => languageNames[t.language] || t.language) || [],
    datasets: [
      {
        label: t('statistics.charts.translations_by_language'),
        data: statistics?.translationsByLanguage?.map(t => t.count) || [],
        backgroundColor: [
          '#0A0C0B',
          '#7BD5B5',
          '#5a9e8a',
          '#687d76'
        ]
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };



  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#7BD5B5]" />
        <span className="ml-3 text-[#687d76]">{t('statistics.dashboard.loading')}</span>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <BarChart3 className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <p className="text-yellow-800">{t('statistics.dashboard.no_data')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
  <div>
    <h2 className="text-2xl font-bold text-[#0A0C0B] mb-1 sm:mb-2">
      {t('statistics.dashboard.title')}
    </h2>
    <p className="text-[#687d76]">{t('statistics.dashboard.subtitle')}</p>
  </div>

  <div className="flex flex-wrap gap-2">
    {(['7d', '30d', 'all'] as const).map((range) => (
      <button
        key={range}
        onClick={() => setTimeRange(range)}
        className={`px-4 py-2 rounded-lg font-medium transition-colors flex-1 sm:flex-none text-center ${
          timeRange === range
            ? 'bg-[#0A0C0B] text-white'
            : 'bg-gray-100 text-[#0A0C0B] hover:bg-gray-200'
        }`}
      >
        {range === 'all'
          ? t('statistics.dashboard.time_ranges.all')
          : t(`statistics.dashboard.time_ranges.${range}`)}
      </button>
    ))}
  </div>
</div>


      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
       

      

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-[#687d76]">{t('statistics.metrics.qr_scans')}</h3>
            <TrendingUp className="w-5 h-5 text-[#687d76]" />
          </div>
          <p className="text-3xl font-bold text-[#0A0C0B]">{(statistics?.qrScans || 0).toLocaleString()}</p>
          <p className="text-sm text-[#687d76] mt-1">{t('statistics.descriptions.qr_scans')}</p>
        </div>
  <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-[#687d76]">{t('statistics.metrics.total_downloads')}</h3>
            <TrendingUp className="w-5 h-5 text-[#687d76]" />
          </div>
          <p className="text-3xl font-bold text-[#0A0C0B]">{(statistics?.downloadsByDate || []).reduce((sum, item) => sum + (item.count || 0), 0)}</p>
          <p className="text-sm text-[#687d76] mt-1">{t('statistics.descriptions.pdf_downloads')}</p>
        </div>
       
      </div>

    


      {/* Additional Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Scans Over Time */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#0A0C0B] mb-4">{t('statistics.charts.qr_scans_over_time')}</h3>
          <div className="h-64">
            <Line 
              data={{
                labels: statistics?.qrScansByDate?.map(d => new Date(d.date).toISOString().split('T')[0]) || [],
                datasets: [
                  {
                    label: t('statistics.charts.qr_scans_over_time'),
                    data: statistics?.qrScansByDate?.map(d => d.count) || [],
                    backgroundColor: 'rgba(123, 213, 181, 0.1)',
                    borderColor: '#7BD5B5',
                    borderWidth: 2,
                    tension: 0.4
                  }
                ]
              }}
              options={chartOptions}
            />
          </div>
        </div>

        {/* Downloads Over Time */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#0A0C0B] mb-4">{t('statistics.charts.downloads_over_time')}</h3>
          <div className="h-64">
            <Line 
              data={{
                labels: statistics?.downloadsByDate?.map(d => new Date(d.date).toISOString().split('T')[0]) || [],
                datasets: [
                  {
                    label: t('statistics.charts.downloads_over_time'),
                    data: statistics?.downloadsByDate?.map(d => d.count) || [],
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderColor: '#3B82F6',
                    borderWidth: 2,
                    tension: 0.4
                  }
                ]
              }}
              options={chartOptions}
            />
          </div>
        </div>
      </div>


      {/* Popular Items */}
      {statistics?.popularItems && statistics.popularItems?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-[#0A0C0B] mb-4">{t('statistics.popular_items.title')}</h3>
          <div className="space-y-3">
            {statistics.popularItems?.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-[#0A0C0B] text-white flex items-center justify-center font-bold">
                    {index + 1}
                  </span>
                  <span className="font-medium text-[#0A0C0B]">{item.name}</span>
                </div>
                <span className="text-sm text-[#687d76]">{item.views} {t('statistics.popular_items.views_suffix')}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsDashboard;

